import { useState, useEffect, useCallback } from 'react';
import { Loader2, Download, Search, Trash2, UserX, Users, CheckSquare, Square, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const UserDatabaseTab = ({ headers }) => {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [purging, setPurging] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const fetchUsers = useCallback(async (searchQuery = '') => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/users?search=${encodeURIComponent(searchQuery)}&limit=100`, { headers, credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setUsers(data.users || []);
        setTotal(data.total || 0);
      }
    } catch (e) { console.error('Failed to fetch users:', e); }
    finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers(search);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API}/api/analytics/users/export`, { headers, credentials: 'include' });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'taxera_users_export.csv';
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (e) { console.error('Failed to export users:', e); }
    finally { setExporting(false); }
  };

  const handleDelete = async (user) => {
    const identifier = user.full_name || user.name || user.email;
    if (!window.confirm(`Delete user "${identifier}"? This action cannot be undone.`)) return;
    setDeleting(user.user_id);
    try {
      const res = await fetch(`${API}/api/admin/users/${user.user_id}`, {
        method: 'DELETE', headers, credentials: 'include',
      });
      if (res.ok) { await fetchUsers(search); }
      else { const err = await res.json(); alert(err.detail || 'Failed to delete user'); }
    } catch (e) { alert('Failed to delete user'); }
    finally { setDeleting(null); }
  };

  const handlePurgeTest = async () => {
    const count = users.filter(u => isTestUser(u)).length;
    if (!window.confirm(`PURGE ${count} test/automated users?\n\nThis will delete all users with emails/names containing: TEST_, test_, e2e_, uitest_, example.com, company.com\n\nYour owner account will NOT be affected.`)) return;
    if (!window.confirm('Are you absolutely sure? This cannot be undone.')) return;
    setPurging(true);
    try {
      const res = await fetch(`${API}/api/admin/users/bulk/purge-test`, {
        method: 'DELETE',
        headers: { ...headers, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ patterns: ['TEST_', 'test_', 'e2e_', 'uitest_', 'example.com', 'company.com'] }),
      });
      if (res.ok) {
        const data = await res.json();
        alert(`Purged ${data.deleted} test users.`);
        setSelected(new Set());
        await fetchUsers(search);
      } else { const err = await res.json(); alert(err.detail || 'Purge failed'); }
    } catch (e) { console.error('Failed to purge test users:', e); }
    finally { setPurging(false); }
  };

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} selected users? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      const res = await fetch(`${API}/api/admin/users/bulk/delete`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ user_ids: [...selected] }),
      });
      if (res.ok) {
        const data = await res.json();
        alert(`Deleted ${data.deleted} users.`);
        setSelected(new Set());
        await fetchUsers(search);
      } else { const err = await res.json(); alert(err.detail || 'Bulk delete failed'); }
    } catch (e) { console.error('Failed to bulk delete:', e); }
    finally { setBulkDeleting(false); }
  };

  const isTestUser = (u) => {
    const email = (u.email || '').toLowerCase();
    const name = (u.full_name || u.name || '').toLowerCase();
    return email.includes('test') || email.includes('e2e') || email.includes('example.com') || email.includes('company.com') || name.includes('test');
  };

  const toggleSelect = (userId) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === users.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(users.map(u => u.user_id)));
    }
  };

  const testUserCount = users.filter(u => isTestUser(u)).length;

  return (
    <div data-testid="admin-userdb">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-xl font-bold text-taxera-charcoal flex items-center gap-2">
            <Users className="w-5 h-5" /> User Database
          </h3>
          <p className="text-sm text-slate-500 mt-1">{total} users total{testUserCount > 0 && <span className="text-amber-500 ml-2">({testUserCount} test accounts detected)</span>}</p>
        </div>
        <div className="flex items-center gap-2">
          {testUserCount > 0 && (
            <button onClick={handlePurgeTest} disabled={purging}
              className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg text-sm font-semibold hover:bg-amber-100 transition-all disabled:opacity-50"
              data-testid="purge-test-btn">
              {purging ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <AlertTriangle className="w-3.5 h-3.5" />}
              Purge Test Data
            </button>
          )}
          <button onClick={handleExport} disabled={exporting}
            className="inline-flex items-center gap-2 px-4 py-2 bg-taxera-teal text-white rounded-lg text-sm font-semibold hover:bg-taxera-teal/90 transition-all disabled:opacity-50"
            data-testid="export-users-btn">
            {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            Export CSV
          </button>
        </div>
      </div>

      <form onSubmit={handleSearch} className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or email..."
            className="w-full border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-taxera-teal"
            data-testid="user-search-input" />
        </div>
      </form>

      {/* Bulk actions bar */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 mb-3 p-3 bg-red-50 border border-red-200 rounded-xl">
          <span className="text-sm font-semibold text-red-700">{selected.size} selected</span>
          <button onClick={handleBulkDelete} disabled={bulkDeleting}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs font-semibold hover:bg-red-700 disabled:opacity-50"
            data-testid="bulk-delete-btn">
            {bulkDeleting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
            Delete Selected
          </button>
          <button onClick={() => setSelected(new Set())} className="text-xs text-red-600 hover:underline">
            Clear selection
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-taxera-blue" /></div>
      ) : (
        <div className="space-y-2">
          {/* Select all header */}
          {users.length > 0 && (
            <div className="flex items-center gap-3 px-4 py-2 text-xs text-slate-400">
              <button onClick={toggleSelectAll} className="hover:text-taxera-blue transition-colors" data-testid="select-all-btn">
                {selected.size === users.length ? <CheckSquare className="w-4 h-4 text-taxera-blue" /> : <Square className="w-4 h-4" />}
              </button>
              <span className="flex-1">Name</span>
              <span className="hidden md:block w-32">Company</span>
              <span className="hidden md:block w-24">Source</span>
              <span className="hidden md:block w-16 text-center">Diag.</span>
              <span className="w-8" />
            </div>
          )}

          {users.map((u, i) => {
            const isTest = isTestUser(u);
            return (
              <div
                key={u.user_id || i}
                className={`flex items-center gap-3 rounded-xl border p-4 hover:shadow-sm transition-all ${
                  isTest ? 'bg-amber-50/50 border-amber-200/60' : 'bg-white border-slate-200'
                } ${selected.has(u.user_id) ? 'ring-2 ring-taxera-blue/30' : ''}`}
                data-testid={`userdb-row-${i}`}
              >
                {/* Checkbox */}
                <button onClick={() => toggleSelect(u.user_id)} className="shrink-0 hover:text-taxera-blue transition-colors">
                  {selected.has(u.user_id) ? <CheckSquare className="w-4 h-4 text-taxera-blue" /> : <Square className="w-4 h-4 text-slate-300" />}
                </button>

                {/* User info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-taxera-charcoal truncate">
                      {u.full_name || u.name || 'Unknown'}
                    </span>
                    <span className={`text-2xs px-2 py-0.5 rounded-full font-semibold ${
                      u.provider === 'google' ? 'bg-blue-50 text-blue-600' : 'bg-slate-100 text-slate-500'
                    }`}>
                      {u.provider || 'email'}
                    </span>
                    {isTest && (
                      <span className="text-2xs px-2 py-0.5 rounded-full font-semibold bg-amber-100 text-amber-600">TEST</span>
                    )}
                    {u.is_active === false && (
                      <span className="text-2xs px-2 py-0.5 rounded-full font-semibold bg-red-50 text-red-600">Blocked</span>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5 truncate">{u.email}</p>
                </div>

                {/* Company */}
                <div className="hidden md:block w-32 shrink-0">
                  <span className="text-xs text-slate-500 truncate block">{u.lead_company || '—'}</span>
                </div>

                {/* Lead source */}
                <div className="hidden md:block w-24 shrink-0">
                  {u.has_lead ? (
                    <span className="text-2xs px-2 py-0.5 rounded-full font-semibold bg-taxera-teal/10 text-taxera-teal capitalize">{u.lead_source || 'lead'}</span>
                  ) : (
                    <span className="text-2xs text-slate-400">—</span>
                  )}
                </div>

                {/* Diagnostics count */}
                <div className="hidden md:block w-16 shrink-0 text-center">
                  <span className="text-xs text-slate-500">{u.diagnostics_count || 0}</span>
                </div>

                {/* Delete */}
                <button
                  onClick={() => handleDelete(u)}
                  disabled={deleting === u.user_id}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50 shrink-0"
                  title="Delete user"
                  data-testid={`userdb-delete-${i}`}
                >
                  {deleting === u.user_id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </button>
              </div>
            );
          })}
          {users.length === 0 && (
            <div className="text-center py-16 text-slate-400">
              <UserX className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">No users found</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UserDatabaseTab;
